import React, { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'

import clsx from 'clsx'
import Highlighter from 'react-highlight-words'
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, sortableKeyboardCoordinates, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { makeStyles, useTheme } from '@mui/styles'
import { emphasize } from '@mui/material/styles'
import Creatable from 'react-select/creatable'
import { components } from 'react-select'
import PerfectScrollbar from 'react-perfect-scrollbar'
import lang from '@/lang'

import { defaultStyles, inlineStyles } from '../select-input/select-input.styles'
import { getContrastText } from '@/modules/base/helpers/getContrastText'
import ColorBubble from '../color-bubble/color-bubble'
import RequiredMark from '@/modules/base/components/texts/required-mark/required-mark'

const useStyles = makeStyles(theme => ({
  labelWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    '& a': {
      fontSize: '12px',
    },
  },
  label: {
    display: 'flex',
    alignSelf: 'flex-start',
    color: theme.text,
    fontSize: '13px',
    marginBottom: '10px',
    '&[disabled]': {
      color: theme.disabledColor,
    },
  },
  matchedText: {
    fontWeight: 'bold',
    background: 'transparent',
  },
  newOption: {
    color: theme.primaryDark,
  },
  colorBubbleWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  valueLabel: {
    display: 'block',
    ...theme.mixins.truncateText,
  },
}))

const ACTION_TYPES = Object.freeze({
  selectOption: 'select-option',
  deselectOption: 'deselect-option',
  removeValue: 'remove-value',
  popValue: 'pop-value',
  setValue: 'set-value',
  clear: 'clear',
  createOption: 'create-option',
})

const EmptyPlaceholder = () => <div />

function SortableValueContainer(containerProps) {
  const value = containerProps.getValue()
  const valueLength = (value || []).length
  const items = useMemo(
    () => Array.from({ length: valueLength }, (_, i) => i),
    [valueLength],
  )
  return (
    <SortableContext items={items} strategy={horizontalListSortingStrategy}>
      <components.ValueContainer {...containerProps} />
    </SortableContext>
  )
}

function SortableMultiValue(props) {
  const { selectProps, data } = props
  const getOptionFixed = selectProps?.getOptionFixed

  const onMouseDown = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  if (getOptionFixed?.(data)) {
    return <components.MultiValue {...props} />
  }

  const innerProps = { ...props.innerProps, onMouseDown }
  return (
    <SortableMultiValueInner {...props} innerProps={innerProps} />
  )
}

function SortableMultiValueInner(props) {
  const { index, innerProps } = props
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: index })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: 'inline-block',
    maxWidth: '100%',
    minWidth: 0,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <components.MultiValue {...props} innerProps={innerProps} />
    </div>
  )
}

const Input = props => {
  const { placeholder } = props.selectProps
  return <components.Input placeholder={placeholder} {...props} />
}

const PerfectScrollbarMenuList = props => {
  return (
    <PerfectScrollbar options={{ suppressScrollX: true }} style={{ maxHeight: props.maxHeight }}>
      <components.MenuList {...props} />
    </PerfectScrollbar>
  )
}

const MultiValueRemove = props => {
  const { selectProps: { getOptionFixed }, data } = props
  const isFixed = getOptionFixed && getOptionFixed(data)

  if (isFixed) return <div />

  return (
    <components.MultiValueRemove {...props} />
  )
}

// Stable fallback, so `customStyles` below is not rebuilt on every render of an unstyled select.
const NO_EXTERNAL_STYLES = {}

const MultiSelectInput = (props) => {
  const classes = useStyles(props)
  const theme = useTheme()
  const {
    onValueChange,
    sortable = true,
    value,
    isInline,
    label,
    readOnly,
    alwaysShowPlaceholder = false,
    styles: externalStyles = NO_EXTERNAL_STYLES,
    getOptionFixed: customOptionFixed,
    components: passedComponents,
    onClearLink,
    className,
    withoutPerfectScrollbar,
    customLabelClass,
    showRequiredMark = false,
    dataQsId,
    showColorBubble = false,
  } = props

  const [selected, setSelected] = useState([])

  const getOptionFixed = customOptionFixed || ((option) => { return option.isFixed })

  const customStyles = useMemo(() => {
    const mainStyle = isInline ? inlineStyles : defaultStyles

    return {
      ...mainStyle,
      ...externalStyles,
      control: (base, state) => {
        // An external control style gets the adapted style, as in SelectInput - handed react-select's
        // raw one, spreading it brought back the blue focus shadow and border.
        const mainControl = mainStyle.control(base, state)
        return {
          ...mainControl,
          ...(externalStyles?.control ? externalStyles.control(mainControl, state) : null),
          ...(isInline
            ? ({
              borderBottom: null,
              borderColor: 'transparent',
              border: `solid 1px ${state.selectProps.menuIsOpen ? theme.activeInputBorder : 'transparent'}`,
              '&:hover': {
                borderColor: null,
                border: null,
              },
              transition: null,
            })
            : null),
          ...{
            minHeight: 45,
            height: null,
            ':hover input::placeholder': {
              color: theme.primary,
            },
          },
          ...(readOnly && ({
            border: `1px solid ${theme.disabledBorderColor}`,
            'input::placeholder': {
              color: theme.disabledColor,
            },
          })),
        }
      },
      input: (base, state) => {
        const newStyle = {
          ...base,
          '> input': {
            width: 'fit-content !important',
          },
          ...(isInline
            ? {
              borderStyle: 'dashed',
              borderWidth: '1px',
              borderColor: theme.borderColor,
              padding: '5px',
              paddingLeft: '8px',
              borderRadius: '25px',
              display: 'flex',
              alignItems: 'center',
            }
            : null),
        }
        return externalStyles?.input ? externalStyles.input(newStyle, state) : newStyle
      },
      menu: (styles, state) => ({
        ...mainStyle.menu(styles, state),
        padding: 1,
      }),
      menuList: (styles, state) => ({
        ...mainStyle.menuList(styles, state),
        ...(!withoutPerfectScrollbar
          ? {
            position: 'relative',
            overflow: 'hidden',
            maxHeight: 'unset',
          }
          : null
        ),
      }),
      valueContainer: styles => ({
        ...styles,
        minWidth: 0,
        ...(isInline
          ? { padding: 0 }
          : {
            paddingRight: 2,
            paddingLeft: 13, // + margin 2px = 15px
          }),
      }),
      multiValue: (styles, props) => {
        const newStyle = {
          ...styles,
          zIndex: 1300, // to be over MuiDrawer-modal
          margin: '4px 2px',
          padding: 5,
          borderRadius: 25,
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: theme.borderColor,
          backgroundColor: props.data.backgroundColor || (isInline ? theme.backgroundColor : '#FFF'),
          height: 30,
          alignItems: 'center',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }
        const custom = externalStyles.multiValue
        return custom ? custom(newStyle, props) : newStyle
      },
      multiValueLabel: (styles, props) => {
        const { data: { backgroundColor = styles.backgroundColor, color } } = props
        const isFixed = props.selectProps.getOptionFixed(props.data)

        const newStyle = {
          ...styles,
          paddingRight: isFixed ? 6 : null,
          color: color || backgroundColor ? getContrastText(backgroundColor) : theme.textColor,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }

        const custom = externalStyles.multiValueLabel
        return custom ? custom(newStyle, props) : newStyle
      },
      multiValueRemove: (styles, props) => {
        const { data: { backgroundColor, color } } = props

        const textColor = color || backgroundColor ? getContrastText(backgroundColor) : theme.textColor
        const hoverBackgroundColor = backgroundColor ? emphasize(backgroundColor) : theme.primarySuperLight

        const newStyle = {
          ...styles,
          borderRadius: '50%',
          color: textColor,
          padding: 2,
          flexShrink: 0,
          ':hover': {
            backgroundColor: hoverBackgroundColor,
            color: textColor,
          },
          cursor: 'pointer',
        }
        const custom = externalStyles.multiValueRemove
        return custom ? custom(newStyle, props) : newStyle
      },
      placeholder: (styles, state) => {
        // A disabled control already greys its border and label - the placeholder has to follow.
        const placeholderColor = state.isDisabled
          ? theme.disabledColor
          : (state.isFocused ? theme.lighterGrey : theme.primary)
        const newStyle = {
          ...styles,
          width: 'fit-content',
          color: placeholderColor,
          display:
              state.isFocused || state.isSelected || state.selectProps.inputValue
                ? 'none'
                : 'block',
        }
        return externalStyles?.placeholder ? externalStyles.placeholder(newStyle, state) : newStyle
      },
    }
  // Every value read above - with `isInline` alone, a `styles` prop that changes after mount (an
  // error border, say) or a toggled `readOnly` would never reach the control.
  }, [isInline, externalStyles, readOnly, withoutPerfectScrollbar, theme])

  const handleChange = (newValue, actionMeta) => {

    if (actionMeta.removedValue && getOptionFixed(actionMeta.removedValue)) return

    switch (actionMeta) {
      case 'clear':
        newValue = selected.filter(item => getOptionFixed(item))
        break
    }

    setSelected(newValue)
    onValueChange && onValueChange(newValue, actionMeta)
  }

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event
    if (over == null || active.id === over.id) return
    const currentValue = value === undefined ? selected : value
    if (!currentValue?.length) return
    const oldIndex = Number(active.id)
    const newIndex = Number(over.id)
    if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return
    const newValue = arrayMove(currentValue, oldIndex, newIndex)
    if (value === undefined) {
      setSelected(newValue)
    }
    onValueChange?.(newValue, { action: ACTION_TYPES.setValue })
  }, [value, selected, onValueChange])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const formatOptionLabel = (option, { context, inputValue }) => {
    const { label, backgroundColor, __isNew__: isNew } = option
    const colorBubble = showColorBubble ? <ColorBubble color={backgroundColor || theme.newTagColor} /> : null

    if (context === 'value') return <span title={label || ''} className={classes.valueLabel}>{label}</span>

    return (
      <div className={classes.colorBubbleWrapper}>
        {colorBubble}
        <Highlighter
          searchWords={[inputValue]}
          textToHighlight={label || ''}
          highlightClassName={clsx(classes.matchedText, isNew && classes.newOption)}
          autoEscape={true}
        />
      </div>
    )
  }

  const labelEl = !isInline && label
    ? <label className={clsx(classes.label, customLabelClass)} disabled={readOnly}>{label}{showRequiredMark && <RequiredMark />}</label>
    : null

  const components = useMemo(() => {
    const comps = {
      ...(sortable ? { ValueContainer: SortableValueContainer, MultiValue: SortableMultiValue } : null),
      ...(!withoutPerfectScrollbar ? { MenuList: PerfectScrollbarMenuList } : null),
      MultiValueRemove,
      ...passedComponents,
    }
    if (alwaysShowPlaceholder) {
      comps.Input = Input
      comps.Placeholder = EmptyPlaceholder
    }
    return comps
  }, [sortable, withoutPerfectScrollbar, passedComponents, alwaysShowPlaceholder])

  const selectProps = {
    isMulti: true,
    closeMenuOnSelect: false,
    value: value === undefined ? selected : value,
    onChange: handleChange,
    getOptionFixed,
    ...props,
    styles: customStyles,
    isDisabled: readOnly,
    formatOptionLabel,
    formatCreateLabel: lang.selectInput.createLabel,
    components,
  }

  const select = (
    <Creatable
      {...selectProps}
    />
  )

  return (
    <div className={className} data-qs-id={dataQsId}>
      {labelEl && <div className={classes.labelWrapper}>
        {labelEl}
        {onClearLink && <a className={clsx(externalStyles.clearLink)} onClick={() => onClearLink && onClearLink()}>{lang.generics.reset}</a>}
      </div>}
      {sortable
        ? (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            {select}
          </DndContext>
        )
        : select}
    </div>
  )
}

MultiSelectInput.propTypes = {
  value: PropTypes.array,
  label: PropTypes.string,
  /** Asterisk behind the label of a required field. */
  showRequiredMark: PropTypes.bool,
  placeholder: PropTypes.string,
  alwaysShowPlaceholder: PropTypes.bool,
  sortable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  allowCreate: PropTypes.bool,
  isLoading: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.object),
  isClearable: PropTypes.bool,
  isValidNewOption: PropTypes.func,
  formatCreateLabel: PropTypes.func,
  noOptionsMessage: PropTypes.func,
  getNewOptionData: PropTypes.func,
  getOptionValue: PropTypes.func,
  getOptionLabel: PropTypes.func,
  onValueChange: PropTypes.func,
  onClearLink: PropTypes.func,
  customLabelClass: PropTypes.any,
  dataQsId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  isSearchable: PropTypes.bool,
  components: PropTypes.object,
}

export default MultiSelectInput

export {
  MultiSelectInput,
  ACTION_TYPES,
}
